import { useState } from "react";
import { toast } from "react-toastify";
import { BiChat } from "react-icons/bi";
import { FiTrash2 } from "react-icons/fi";
import PageHeader from "../../components/PageHeader";
import Spinner from "../../../Spinner/Spinner";
import useFetchmessages from "../../../marketplace_admin/libs/useFetchmessages";
import useSendBroadcast from "../../../marketplace_admin/libs/useSendBroadcast";
import useDeleteBroadMsg from "../../../marketplace_admin/libs/useDeleteMessage";

const AdminMessagesPage = () => {
  const { adminMessages, isLoading, isError } = useFetchmessages();
  const { mutateAsync: sendBroadcast, isLoading: isSending } = useSendBroadcast();
  const { isDeleting, handleDeleteMessage } = useDeleteBroadMsg();
  const [composeText, setComposeText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!composeText.trim()) {
      toast.error("Enter a message");
      return;
    }
    try {
      await sendBroadcast(composeText.trim());
      setComposeText("");
      toast.success("Broadcast sent to all users");
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || "Failed to send broadcast");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await handleDeleteMessage(deleteTarget);
      setDeleteTarget(null);
      toast.success("Message deleted");
    } catch {
      toast.error("Could not delete message");
    }
  };

  return (
    <>
      <PageHeader
        title="Broadcast"
        subtitle="Send announcements to all marketplace users"
        icon={BiChat}
      />

      <section className="dashboard-card dashboard-broadcast-compose">
        <h2 className="dashboard-card__title">Compose message</h2>
        <form onSubmit={handleSend}>
          <label className="dashboard-form-field dashboard-form-field--full">
            <span>Message</span>
            <textarea
              rows={4}
              placeholder="Write a message to all users…"
              value={composeText}
              onChange={(e) => setComposeText(e.target.value)}
            />
          </label>
          <div className="dashboard-form-actions">
            <button type="submit" className="dashboard-btn dashboard-btn--primary" disabled={isSending}>
              {isSending ? "Sending…" : "Send to all users"}
            </button>
          </div>
        </form>
      </section>

      <section className="dashboard-card" style={{ marginTop: 24 }}>
        <h2 className="dashboard-card__title">Sent broadcasts</h2>
        {isLoading && (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spinner />
          </div>
        )}
        {isError && (
          <div className="dashboard-banner dashboard-banner--danger">Could not load broadcast history.</div>
        )}
        {!isLoading && !isError && (!adminMessages || adminMessages.length === 0) && (
          <p className="dashboard-form-hint" style={{ margin: 0 }}>No broadcasts sent yet.</p>
        )}
        <ul className="dashboard-broadcast-list">
          {(adminMessages || []).map((message) => (
            <li key={message.id} className="dashboard-broadcast-item">
              <div className="dashboard-broadcast-item__header">
                <div>
                  <strong>
                    {message?.sender?.first_name} {message?.sender?.last_name}
                  </strong>
                  <span className="dashboard-broadcast-item__time">
                    {message?.timestamp ? new Date(message.timestamp).toLocaleString() : ""}
                  </span>
                </div>
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn--secondary dashboard-btn--icon"
                  aria-label="Delete broadcast"
                  onClick={() => setDeleteTarget(message.id)}
                >
                  <FiTrash2 />
                </button>
              </div>
              <p className="dashboard-broadcast-item__body">{message?.content}</p>
            </li>
          ))}
        </ul>
      </section>

      {deleteTarget && (
        <div className="dashboard-modal-backdrop" role="dialog" aria-modal="true">
          <div className="dashboard-modal">
            <h3>Delete broadcast?</h3>
            <p>This message will be removed for all users.</p>
            <div className="dashboard-modal__actions">
              <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="dashboard-btn dashboard-btn--danger"
                disabled={isDeleting}
                onClick={confirmDelete}
              >
                {isDeleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminMessagesPage;
